import React from 'react';
import { call, takeEvery } from 'redux-saga/effects';
import {
  UserForm,
  mountForm,
  unmountForm,
  deleteUser,
} from '@kineticdata/react';
import { generateFormButtons, FormError } from '@kineticdata/ui';
import { regSaga, regRoute, action } from '../../../store';
import { navigate } from '../../../root';
import { TimeAgoAndUser } from '../../common/TimeAgoAndUser';
import { Link, useParams } from 'react-router-dom';
import { ChangePasswordField } from '../../common/forms/ChangePasswordField';
import { openModal } from '../../common/ConfirmationModal';
import { FormLayout, userDeleteConfirmOptions } from './index';

const formKey = (spaceSlug, username) => `${spaceSlug}-${username}-user-form`;

regRoute('USER_EDIT_PAGE', '/spaces/edit/:spaceSlug/users/edit/:username');
regSaga(
  takeEvery('ENTER_USER_EDIT_PAGE', function* ({ payload }) {
    const { spaceSlug, username } = payload.params;
    yield call(mountForm, formKey(spaceSlug, username));
  }),
);

regSaga(
  takeEvery('LEAVE_USER_EDIT_PAGE', function* ({ payload }) {
    const { spaceSlug, username } = payload.params;
    yield call(unmountForm, formKey(spaceSlug, username));
  }),
);

regSaga(
  takeEvery('DELETE_USER', function* ({ payload }) {
    const { username, spaceSlug } = payload;
    const { user } = yield call(deleteUser, { username, spaceSlug });
    if (user) {
      navigate(`/spaces/edit/${spaceSlug}/users`, { replace: true });
    }
  }),
);

const handleSave =
  ({ spaceSlug }) =>
  () => {
    navigate(`/spaces/edit/${spaceSlug}/users`);
  };

export const SpaceUsersEdit = () => {
  const { spaceSlug, username } = useParams();

  const handleDelete = () =>
    openModal({
      ok: action('DELETE_USER', { username, spaceSlug }),
      ...userDeleteConfirmOptions,
    });

  return (
    <UserForm
      spaceSlug={spaceSlug}
      username={username}
      alterFields={{ changePassword: { component: ChangePasswordField } }}
      fieldSet={[
        'username',
        'enabled',
        'spaceAdmin',
        'email',
        'displayName',
        'changePassword',
        'password',
        'passwordConfirmation',
        'allowedIps',
      ]}
      components={{
        FormLayout,
        FormError,
        FormButtons: generateFormButtons({
          handleDelete,
          submitLabel: 'Update User',
          cancelPath: `/spaces/edit/${spaceSlug}/users`,
        }),
      }}
      onSave={handleSave}
    >
      {({ form, initialized, bindings: { user: userBindings } }) =>
        initialized && (
          <div className="app-content__main">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/">Spaces</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={`/spaces/edit/${spaceSlug}`}>{spaceSlug}</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={`/spaces/edit/${spaceSlug}/users`}>Users</Link>
                </li>
                <li className="breadcrumb-item active">
                  {userBindings.get('username')}
                </li>
              </ol>
            </nav>
            <div className="heading">
              <h1>
                Edit{' '}
                {userBindings.get('displayName') ||
                  userBindings.get('username')}
              </h1>
            </div>
            <dl className="metadata">
              <div>
                <dt>Created:</dt>
                <dd>
                  <TimeAgoAndUser
                    time={userBindings.get('createdAt')}
                    username={userBindings.get('createdBy')}
                  />
                </dd>
              </div>
              <div>
                <dt>Updated:</dt>
                <dd>
                  <TimeAgoAndUser
                    time={userBindings.get('updatedAt')}
                    username={userBindings.get('updatedBy')}
                  />
                </dd>
              </div>
            </dl>
            <div className="content-container">{form}</div>
          </div>
        )
      }
    </UserForm>
  );
};
